import {
	BackgroundAttributes,
	ResponsiveBreakpoint,
} from '@redfox-thank-you/control-groups';
import { createAttributeManager, StyleRenderer } from '@redfox-thank-you/utils';
import { InnerBlocks, useBlockProps } from '@wordpress/block-editor';
import { useSelect } from '@wordpress/data';
import { store as editorStore } from '@wordpress/editor';
import React from 'react';

import { Settings } from './settings';
import { generateInlineContainerCSS } from './style';
import type {
	InlineContainerEditProps,
	ResponsiveLayoutAttributes,
} from './types';

/**
 * Inline Container block edit component.
 */
export const Edit: React.FC< InlineContainerEditProps > = ( {
	attributes,
	setAttributes,
} ) => {
	// Create attribute manager with utility functions
	const { getAttribute, updateAttributes } = createAttributeManager(
		attributes,
		setAttributes,
	);

	// Get WordPress editor device type for responsive preview
	const deviceType = useSelect( ( select ) => {
		const editorSelect = select(
			editorStore,
		) as unknown as import('@wordpress/editor').EditorStore;
		return editorSelect?.getDeviceType?.() ?? 'Desktop';
	}, [] );

	// Map WordPress device types to our breakpoint types
	const deviceTypeToBreakpoint = ( type: string ): ResponsiveBreakpoint => {
		switch ( type ) {
			case 'Tablet':
				return 'tablet';
			case 'Mobile':
				return 'mobile';
			default:
				return 'desktop';
		}
	};

	const currentBreakpoint = deviceTypeToBreakpoint( deviceType as string );

	// Get responsive layout settings
	const responsiveLayoutSettings = getAttribute< ResponsiveLayoutAttributes >(
		'settings.layout',
		{},
	);

	// Get current breakpoint settings for editor preview
	const currentLayoutSettings =
		responsiveLayoutSettings[ currentBreakpoint ] || {};

	// Extract layout settings for basic editor preview with defaults
	const flexDirection = currentLayoutSettings.flexDirection || 'row';
	const flexWrap = currentLayoutSettings.flexWrap || 'nowrap';
	const justifyContent = currentLayoutSettings.justifyContent || 'flex-start';
	const alignItems = currentLayoutSettings.alignItems || 'stretch';
	const rowGap = currentLayoutSettings.rowGap || '';
	const columnGap = currentLayoutSettings.columnGap || '';

	// Generate unique block class based on attributes
	const generateBlockClass = () => {
		const cleanName = 'inline-container';
		const hash = Math.random().toString( 36 ).substring( 2, 10 );
		return `redfox-thank-you-${ cleanName }-${ hash }`;
	};

	const [ blockClass ] = React.useState( () => generateBlockClass() );

	// Check if any overlay is enabled across responsive breakpoints
	const hasAnyOverlayEnabled = (
		backgroundStyle: BackgroundAttributes,
	): boolean => {
		const breakpoints = [ 'desktop', 'tablet', 'mobile' ] as const;
		return breakpoints.some( ( breakpoint ) => {
			const breakpointValues = backgroundStyle?.[ breakpoint ];
			return breakpointValues?.backgroundOverlay?.enabled;
		} );
	};

	const blockBackgroundStyle = getAttribute< BackgroundAttributes >(
		'style.block.background',
	);
	const overlayEnabled = hasAnyOverlayEnabled( blockBackgroundStyle );

	// Generate gap CSS value for editor preview
	const generateGapValue = () => {
		if ( rowGap && columnGap ) {
			return `${ rowGap } ${ columnGap }`;
		} else if ( rowGap && ! columnGap ) {
			return `${ rowGap } 0`;
		} else if ( columnGap && ! rowGap ) {
			return `0 ${ columnGap }`;
		}
		return '';
	};

	const blockProps = useBlockProps( {
		className: blockClass,
		style: {
			// Basic editor preview styles - full styles handled via StyleRenderer
			display: 'flex',
			flexDirection:
				flexDirection as React.CSSProperties[ 'flexDirection' ],
			flexWrap: flexWrap as React.CSSProperties[ 'flexWrap' ],
			justifyContent:
				justifyContent as React.CSSProperties[ 'justifyContent' ],
			alignItems: alignItems as React.CSSProperties[ 'alignItems' ],
			gap: generateGapValue(),
			position: 'relative',
		},
	} );

	return (
		<>
			<StyleRenderer
				css={ generateInlineContainerCSS( attributes, blockClass ) }
				blockId={ blockClass }
			/>
			<Settings
				updateAttributes={ updateAttributes }
				getAttribute={ getAttribute }
			/>

			<div { ...blockProps }>
				{ /* Background Overlay - responsive aware */ }
				{ overlayEnabled && <div className="background-overlay" /> }

				<InnerBlocks />
			</div>
		</>
	);
};
